'use strict';

define('vb/private/action/codeChainRunner',[
  'vb/private/action/baseChainRunner', 'vb/private/constants', 'vb/private/utils',
  'vb/binding/expression', 'vb/action/action', 'vb/action/builtin/assignVariablesAction',
  'vb/private/action/actionRunner', 'vb/private/action/actionChainUtils'],
(BaseChainRunner, Constants, Utils, Expression, Action, AssignVariablesAction,
  ActionRunner, ActionChainUtils) => {
  class CodeChainRunner extends BaseChainRunner {
    createExecutionContext(context = {}, callingContexts = {}) {
      const executionContext = super.createExecutionContext(context, callingContexts);

      // for JS chains, sanitize the execution context and remove shortcuts, so we don't expose unnecessary
      // information
      ['$chains', '$imports', '$listeners', '$metadata'].forEach((scopeName) => {
        delete executionContext[scopeName];
      });

      // proxy context to handle direct assignments and guard against illegal assignments
      return CodeChainRunner.proxyContext(executionContext);
    }

    /**
     * Create an instance of code chain.
     *
     * @param ChainClass the class for the chain
     * @returns {*}
     *
     * @overrides BaseChainRunner.createChain
     */
    // eslint-disable-next-line class-methods-use-this
    createChain(chain) {
      const ChainClass = Utils.getDefaultModule(chain);

      if (typeof ChainClass === 'function') {
        return new ChainClass();
      }

      throw new Error(`Failed to create code chain from ${JSON.stringify(ChainClass)}.`);
    }

    /**
     * Retrieves the scope of the action chain's target container.
     * @param availableContexts the available contexts such as $application, $page, etc
     * @returns {Object} scope
     */
    // eslint-disable-next-line class-methods-use-this
    getTargetContainerScope(availableContexts) {
      const internalContext = availableContexts && ActionChainUtils.getInternalContext(availableContexts);
      const currentScope = internalContext && Utils.getScopeName(internalContext.targetContainer);
      return currentScope && availableContexts[currentScope];
    }

    /**
     * Load the JS source file for the chain located at the given chainPath.
     *
     * @param chainPath location of the source file for the chain
     * @returns {Promise}
     * @overrides BaseChainRunner.loadChainSource
     */
    // eslint-disable-next-line class-methods-use-this
    loadChainSource(chainPath) {
      return Utils.getRuntimeEnvironment().then((re) => re.getModuleResource(chainPath));
    }

    /**
     * Since the result of a code chain is not an outcome, this method wraps it in a success outcome.
     *
     * @param result to be wrapped in a success outcome
     * @returns {{result: *, name: string}}
     * @overrides BaseChainRunner.coerceResultToOutcome
     */
    // eslint-disable-next-line class-methods-use-this
    coerceResultToOutcome(result) {
      return Action.createSuccessOutcome(result);
    }

    /**
     * Wrap the error thrown from a code chain in a failure outcome.
     *
     * @param error error to coerce
     * @returns {{name: string, result: {message: {summary}, error, payload}}|{result, name}|*}
     */
    // eslint-disable-next-line class-methods-use-this
    coerceErrorToOutcome(error) {
      const err = typeof error === 'string' ? new Error(error) : error;
      return Action.createFailureOutcome(err.message, err);
    }

    /**
     * For JS based action chain, fire a notification event to report the error by default.
     *
     * @param error the error to be reported
     * @param context the context necessary to run fireNotificationEventAction
     * @param rethrowError if true, rethrow the error instead of firing a notification event
     * @returns {Promise}
     *
     * @overrides BaseChainRunner.handleError
     */
    // eslint-disable-next-line class-methods-use-this
    handleError(error, context, rethrowError) {
      if (!rethrowError) {
        // ignore the error until we implement a way for the application to register a global error handler
        return undefined;
      }

      // otherwise, rethrow the error
      throw error;
    }

    /**
     * Since a JS chain is not loaded via a fetch call, it cannot trigger a ResourceChangedEvent via
     * the ResourceChangedPlugin if application has been updated so we need to explicitly trigger
     * the ResourceChangedEvent by making a fetch call by returning true here.
     *
     * @returns {boolean}
     * @overrides BaseChainRunner.shouldTriggerResourceChangedEvent
     */
    // eslint-disable-next-line class-methods-use-this
    shouldTriggerResourceChangedEvent() {
      return true;
    }

    /**
     * Wrap executionContext in a Proxy that prevents direct modification of scopes. In addition,
     * it translates direct variable assignment into assignVariablesAction.
     *
     * Use getWritableExecutionContext to get a non-proxied version of the executionContext.
     *
     * @param executionContext the execution context to wrap
     * @returns {*}
     */
    static proxyContext(executionContext) {
      const scopeProxies = {};
      const nsProxies = {};
      // Cache only compiled target expressions for this proxied context. getFreshTarget still
      // evaluates the cached expression on every access so direct assignments read fresh values.
      const targetExpressionCache = {
        disposed: false,
        expressions: new Map(),
      };
      const internalContext = ActionChainUtils.getInternalContext(executionContext);
      const currentScope = Utils.getScopeName(internalContext.targetContainer);

      // save the un-proxied version in the internal context so it can be easily retrieved when needed
      internalContext.writableContext = executionContext;
      internalContext.targetExpressionCache = targetExpressionCache;

      return new Proxy(executionContext, {
        get(scopes, scopeName) {
          const scope = scopes[scopeName];

          // can't proxy undefined object
          if (!scope) {
            return undefined;
          }

          // don't proxy the internal context since it's only accessible internally
          if (scope === internalContext) {
            return scope;
          }

          // don't proxy $translations and $webSocket
          if (scopeName === '$translations' || scopeName === '$webSocket') {
            return scope;
          }

          if (CodeChainRunner.isShortcutScope(scopeName)) {
            return CodeChainRunner.proxyNamespace(scopeName, scope, scopes[currentScope],
              currentScope, executionContext, nsProxies, targetExpressionCache);
          }

          scopeProxies[scopeName] = scopeProxies[scopeName]
            || CodeChainRunner.proxyScope(scope, scopeName, executionContext, targetExpressionCache);

          return scopeProxies[scopeName];
        },
        set(scopes, scopeName) {
          if (CodeChainRunner.isShortcutScope(scopeName)) {
            throw new Error(`Cannot write into ${String(currentScope)}.${String(scopeName)}`);
          }
          throw new Error(`Cannot write into ${String(scopeName)}`);
        },
      });
    }

    static isShortcutScope(scopeName) {
      return (scopeName === '$variables' || scopeName === '$functions'
      || scopeName === '$constants' || scopeName === '$enums' || scopeName === '$modules');
    }

    /**
     * Wrap the given scope object in a proxy.
     *
     * @param scope the scope to proxy
     * @param scopeName name of the scope, e.g., $application, $page
     * @param executionContext execution context
     * @param {Object} targetExpressionCache cache for compiled target expressions
     * @returns {*}
     */
    static proxyScope(scope, scopeName, executionContext, targetExpressionCache) {
      const nsProxies = {};

      return new Proxy(scope, {
        get(namespaces, nsName) {
          return CodeChainRunner.proxyNamespace(nsName, namespaces[nsName],
            scope, scopeName, executionContext, nsProxies, targetExpressionCache);
        },
        set(namespaces, nsName) {
          throw new Error(`Cannot write into ${String(scopeName)}.${String(nsName)}`);
        },
      });
    }

    /**
     * Getter to wrap the given namespace object in a proxy
     * @param {*} nsName Name of the namespace
     * @param {*} namespace namespace of the scope
     * @param {*} scope
     * @param {*} scopeName
     * @param {*} executionContext
     * @param {*} nsProxies cache for proxy namespaces
     * @param {Object} targetExpressionCache cache for compiled target expressions
     * @returns {*}
     */
    static proxyNamespace(nsName, namespace, scope, scopeName, executionContext, nsProxies, targetExpressionCache) {
      const nsProxy = nsProxies[nsName];
      if (!nsProxy) {
        if (nsName === Constants.VariableNamespace.VARIABLES || nsName === '$variables') {
          nsProxies[nsName] = CodeChainRunner.proxyVariableAssignment(namespace,
            `${String(scopeName)}.variables`,
            CodeChainRunner.typeResolverFunc(scope), executionContext, true, targetExpressionCache);
          return nsProxies[nsName];
        }

        if (Utils.isObjectOrArray(namespace) && Utils.isPrototypeOfObject(namespace)) {
          nsProxies[nsName] = CodeChainRunner.preventWritesToObject(namespace);
          return nsProxies[nsName];
        }
        return namespace;
      }
      return nsProxy;
    }

    static typeResolverFunc(scope) {
      // look up variable type
      const typeResolver = (varName) => {
        let actualVarName;
        if (Utils.isExtendedType(scope.variables[varName])) {
          actualVarName = `${varName}${Constants.BuiltinVariableName.VALUE}`;
        } else {
          actualVarName = varName;
        }

        const variable = scope.getVariable(actualVarName, Constants.VariableNamespace.VARIABLES);
        if (variable) {
          // for instance factory type, return a generic type definition for the constructorParams
          // which will direct the assignment proxy to use assignVariablesAction for any updates
          if (variable.typeClassification === Constants.VariableClassification.INSTANCE_FACTORY) {
            return { constructorParams: ['any'] };
          }
          return variable.getType();
        }
        return undefined;
      };
      return typeResolver;
    }

    /**
     * Run an assignVariablesAction synchronously.
     *
     * @param executionContext execution context
     * @param params parameters to the assignmentVariableAction
     * @returns {*}
     */
    static runAssignVariableActionSync(executionContext, params) {
      return ActionRunner.runSync(AssignVariablesAction, 'vb/action/builtin/assignVariablesAction',
        executionContext, params, { alias: 'Actions.assignVariable' });
    }

    /**
     * Get the latest value for the given expression.
     *
     * @param expression expression to evaluate
     * @param executionContext context used for evaluation
     * @param {Object} targetExpressionCache cache for compiled target expressions
     */
    static getFreshTarget(expression, executionContext, targetExpressionCache) {
      // If a proxy escapes the chain and is accessed after cleanup, bypass the cache so we do not
      // reuse disposed KO computed values while still preserving the fresh-target fallback behavior.
      const cache = targetExpressionCache && !targetExpressionCache.disposed
        ? targetExpressionCache.expressions : null;
      let targetExpression = cache && cache.get(expression);

      if (!targetExpression) {
        targetExpression = Expression.createFromString(expression,
          ActionChainUtils.getWritableExecutionContext(executionContext), false);

        if (cache) {
          cache.set(expression, targetExpression);
        }
      }

      return targetExpression();
    }

    /**
     * Dispose cached target expressions after the JS action chain has settled.
     * @param executionContext execution context
     */
    // eslint-disable-next-line class-methods-use-this
    cleanupExecutionContext(executionContext) {
      const internalContext = ActionChainUtils.getInternalContext(executionContext);
      const { targetExpressionCache } = internalContext;

      if (targetExpressionCache) {
        // This flag marks the expression cache as closed, not the lifecycle state of the variable context.
        targetExpressionCache.disposed = true;
        try {
          targetExpressionCache.expressions.forEach((targetExpression) => {
            if (targetExpression && typeof targetExpression.dispose === 'function') {
              targetExpression.dispose();
            }
          });
        } finally {
          targetExpressionCache.expressions.clear();
          delete internalContext.targetExpressionCache;
        }
      }
    }

    /**
     * Creates a proxy for the given object that translates direct assignments into assignVariablesAction.
     * @param {Object} obj Object to be proxied
     * @param {string} expression Expression representing the left-hand side of the assignment
     * @param {Function} typeResolver Function used to resolve type definitions for properties
     * @param {Object} executionContext Execution context for running assignVariablesAction
     * @param {boolean} cacheProxy Flag to cache proxies created for sub-properties
     * @param {Object} targetExpressionCache cache for compiled target expressions
     * @returns {Proxy} Proxy wrapping the object, translating assignments to assignVariablesAction
     */
    static proxyVariableAssignment(obj, expression, typeResolver, executionContext, cacheProxy, targetExpressionCache) {
      const cachedProxies = {};

      // Object property keys need to stay string literals when the proxy rebuilds a VB expression. A long
      // numeric-looking id would lose precision if it were emitted as a bare numeric bracket token.
      const quotePropName = (propName) => {
        const propNameString = String(propName);
        return propNameString.indexOf("'") === -1 && propNameString.indexOf('\\') === -1
          ? `['${propNameString}']` : `['${propNameString.replace(/['\\]/g, '\\$&')}']`;
      };
      const isArrayIndex = (propName, target) => {
        if (!Array.isArray(target) || typeof propName === 'symbol') {
          return false;
        }

        // Only arrays keep bare numeric bracket syntax; object maps always use quoted keys.
        const propNameString = String(propName);
        const numericPropName = Number(propNameString);
        return Number.isInteger(numericPropName)
          && numericPropName >= 0
          && numericPropName <= 4294967294
          && String(numericPropName) === propNameString;
      };
      // generate a new expression by appending the given propName to the existing expression
      const newExpr = (propName, target) => (isArrayIndex(propName, target)
        ? `${expression}[${propName}]` : `${expression}${quotePropName(propName)}`);
      const getTarget = (target) => {
        try {
          return CodeChainRunner.getFreshTarget(expression, executionContext, targetExpressionCache);
        } catch (e) {
          // The disposed flag only means the chain expression cache is closed. Keep active-chain errors intact,
          // but let escaped proxies fall back to their captured target after post-cleanup fresh resolution fails.
          if (targetExpressionCache && targetExpressionCache.disposed) {
            return target;
          }
          throw e;
        }
      };

      const proxy = new Proxy(obj, {
        get(target, propName) {
          // Let clone/materialization code identify this as a VB assignment proxy without resolving the
          // fresh target. The target itself may legitimately be undefined.
          if (propName === Constants.IS_CHAIN_ASSIGNMENT_PROXY) {
            return true;
          }

          let propProxy = cacheProxy ? cachedProxies[propName] : null;

          if (!propProxy) {
            // Resolve through the target expression so proxy reads stay fresh. After cleanup, getTarget falls
            // back to the captured target only when post-cleanup fresh resolution fails.
            const freshTarget = getTarget(target);

            if (propName === Constants.CHAIN_PROXY_TARGET) {
              return freshTarget;
            }

            const propValue = freshTarget[propName];

            if (typeof propValue === 'function') {
              return Array.isArray(target) ? CodeChainRunner.proxyArrayOperation(freshTarget, proxy,
                expression, propName, executionContext) : propValue;
            }

            // Explicitly check if propValue is an implicit instance factory variable proxy because
            // Utils.isPrototypeOfObject will return false.
            if ((Utils.isObject(propValue) && (Utils.isPrototypeOfObject(propValue)
              || propValue[Constants.IS_IMPLICIT_INSTANCE_VARIABLE_PROXY]))
              || Array.isArray(propValue) || Utils.isExtendedType(propValue)) {
              const type = typeResolver(propName);

              // don't proxy further if there's no type for this property because it's not
              // part of the VB variable definition
              if (!type) {
                return propValue;
              }

              // get the type definition for a property
              const propTypeResolver = (pn) => {
                if (Utils.isWildcardType(type)) {
                  return 'any';
                }
                if (Utils.isArrayType(type)) {
                  return Utils.getArrayRowType(type);
                }
                if (Utils.isObjectType(type)) {
                  return type[pn];
                }
                return undefined;
              };

              // TODO: This proxy will not cache proxies created for any sub-properties to avoid dealing
              // with stale proxies which can happen due to array operations such as splice and shift.
              propProxy = CodeChainRunner.proxyVariableAssignment(propValue, newExpr(propName, freshTarget),
                propTypeResolver, executionContext, false, targetExpressionCache);
            } else {
              return propValue;
            }

            if (cacheProxy) {
              cachedProxies[propName] = propProxy;
            }
          }

          return propProxy;
        },
        ownKeys(target) {
          return Reflect.ownKeys(getTarget(target));
        },
        getOwnPropertyDescriptor(target, prop) {
          return Reflect.getOwnPropertyDescriptor(getTarget(target), prop);
        },
        set(target, propName, value) {
          const type = typeResolver(propName);

          if (type) {
            const params = {
              variable: newExpr(propName, target),
              value,
              auto: 'always',
              reset: 'empty', // default to empty to simulate the behavior of direct assignment
            };

            // disallow assigning an object to an array
            // Note: This is actually allowed by assignVariablesAction which interprets it as a push but
            // it is just weird when it comes to direct assignment.
            if (Array.isArray(target[propName]) && !Array.isArray(value)) {
              throw new Error(`Cannot assign non-array to an array property, ${String(propName)}`);
            }

            // run the assignVariablesAction in synchronous mode
            CodeChainRunner.runAssignVariableActionSync(executionContext, params);
          } else {
            // directly assign the value if there is no type info because it's not part of the variable
            // definition
            // eslint-disable-next-line no-param-reassign
            target[propName] = value;
          }

          return true;
        },
      });

      return proxy;
    }

    /**
     * Translate an array operation into an assignVariablesAction.
     *
     * @param origArray original unproxied array
     * @param proxiedArray proxied array
     * @param expression expression to be used in the assignVariablesAction.
     * @param operation name of the array operation
     * @param executionContext execution context for the action
     * @returns {function(...[*]=)}
     */
    static proxyArrayOperation(origArray, proxiedArray, expression, operation, executionContext) {
      return (...args) => {
        const params = {
          variable: expression,
          value: args,
          auto: 'always',
          reset: 'none',
        };

        let clonedArray;
        let result;

        switch (operation) {
          case 'push':
            CodeChainRunner.runAssignVariableActionSync(executionContext, params);
            return proxiedArray.length;
          case 'fill':
          case 'pop':
          case 'reverse':
          case 'shift':
          case 'sort':
          case 'splice':
          case 'unshift':
            clonedArray = [...origArray];
            result = clonedArray[operation](...args);

            // @ts-ignore
            params.value = clonedArray;
            params.reset = 'empty';
            CodeChainRunner.runAssignVariableActionSync(executionContext, params);

            if (operation === 'fill' || operation === 'reverse' || operation === 'sort') {
              return proxiedArray;
            }
            return result;
          default:
            return origArray[operation](...args);
        }
      };
    }

    /**
     * Proxy the given object to prevent any writes.
     *
     * @param obj object to proxy
     * @returns {*}
     */
    static preventWritesToObject(obj) {
      return new Proxy(obj, {
        get(target, propName) {
          const propValue = target[propName];

          const config = Object.getOwnPropertyDescriptor(target, propName);
          if (config && config.configurable === false && config.writable === false) {
            return propValue;
          }

          if (Utils.isObjectOrArray(propValue) && Utils.isPrototypeOfObject(propValue)) {
            return CodeChainRunner.preventWritesToObject(propValue);
          }

          return propValue;
        },
        set(target, propName) {
          throw new Error(`Cannot write into ${String(propName)}`);
        },
      });
    }
  }

  return CodeChainRunner;
});

